import React, { useState, useEffect, useMemo } from 'react';
import cx from 'classnames';

import Link from '../../Link';
import Image from 'next/legacy/image';
import arrowDown from '@/images/arrow-down.svg';
import icExternalLink from '@/images/ic_external_link_filled.svg';
import { SubmitSessionDetailsModal } from '../../SubmitSessionDetailsModal';
import { IGlceLink } from '@/types/contentful';
import { ImageData } from '../../../utils/contentful';

interface ResourceItem {
  title: string;
  href: string;
  external?: boolean;
  action?: 'navigate' | 'modal';
}

const RESOURCES_ITEMS: ResourceItem[] = [
  {
    title: 'Submit Session Details',
    href: '/submit-session',
    external: false,
    action: 'modal'
  }
];

export function ResourcesDropdown({
  partnerLogo,
  customLinks,
  partnerDisplayName
}: {
  partnerLogo: ImageData | undefined;
  customLinks?: IGlceLink[];
  partnerDisplayName?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dynamicResourcesItems = useMemo(() => {
    const customLinksItems =
      customLinks?.map((item: IGlceLink) => ({
        title: item.fields.linkText,
        href: item.fields.linkUrl,
        external: item.fields.openInNewTab,
        action: 'navigate' as const
      })) || [];

    const allItems = [...RESOURCES_ITEMS, ...customLinksItems];

    return allItems;
  }, [customLinks]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const dropdown = target.closest('[data-dropdown="resources"]');
      if (!dropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
  };

  const handleItemClick = (item: ResourceItem) => {
    setIsOpen(false);

    if (item.action === 'modal') {
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <>
      <div className="relative" data-dropdown="resources">
        <div
          className="flex cursor-pointer items-center gap-1 font-medium"
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          aria-haspopup="true"
          data-testid="resources-dropdown-trigger"
        >
          <span className="mr-2 font-semibold">Resources</span>
          <div
            className={cx(
              'transition-transform duration-300 ease-[cubic-bezier(0,0.7,0.3,1)]',
              isOpen ? 'rotate-180' : 'rotate-0'
            )}
          >
            <Image src={arrowDown} alt="Dropdown arrow" width={13} height={7} />
          </div>
        </div>

        {isOpen && (
          <>
            {/* Dropdown content */}
            <div
              className="absolute left-1/2 top-full mt-3 w-64 -translate-x-1/2 rounded-2xl bg-white px-6 py-4 shadow-[0_4px_24px_rgba(0,0,0,0.08)]"
              data-testid="resources-dropdown-content"
            >
              <ul className="m-0 list-none p-0 text-base">
                {dynamicResourcesItems.map((item) => (
                  <li key={item.title} className="mb-3 last:mb-0">
                    {item.action === 'modal' ? (
                      <button
                        type="button"
                        className="bg-transparent flex w-full cursor-pointer items-center gap-2 border-none p-0 text-left text-[14px] font-normal text-[#1d252c] no-underline hover:text-[#035C67]"
                        onClick={() => handleItemClick(item)}
                        data-testid={`resources-item-${item.title
                          .toLowerCase()
                          .replace(/\s+/g, '-')
                          .replace(/[^\w-]/g, '')}`}
                      >
                        <span>{item.title}</span>
                      </button>
                    ) : (
                      <Link
                        href={item.href}
                        target={item.external ? '_blank' : '_self'}
                        rel={item.external ? 'noopener noreferrer' : undefined}
                        className="flex items-center gap-2 text-[14px] font-normal text-[#1d252c] no-underline hover:text-[#035C67]"
                        onClick={() => handleItemClick(item)}
                        data-testid={`resources-item-${item.title
                          .toLowerCase()
                          .replace(/\s+/g, '-')
                          .replace(/[^\w-]/g, '')}`}
                      >
                        <span>{item.title}</span>
                        {item.external && (
                          <Image
                            src={icExternalLink}
                            alt="External link"
                            width={13}
                            height={13}
                            className="flex-shrink-0"
                          />
                        )}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {/* Submit Session Details Modal */}
        <SubmitSessionDetailsModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          partnerLogo={partnerLogo}
          partnerDisplayName={partnerDisplayName}
          customLinks={customLinks?.map((item: IGlceLink) => ({
            title: item?.fields?.linkText,
            href: item?.fields?.linkUrl
          }))}
        />
      </div>
    </>
  );
}
